import React, { useState } from 'react';
import { Plus, MessageSquare, Archive, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

export default function ConversationSidebar({
  conversations,
  activeConversation,
  onSelectConversation,
  onNewConversation,
  onArchiveConversation,
  collapsed,
  onToggleCollapse
}) {
  const [hoveredConversation, setHoveredConversation] = useState(null);

  const formatConversationTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return format(date, 'HH:mm');
    } else if (diffInHours < 168) { // 7 days
      return format(date, 'EEE');
    } else {
      return format(date, 'MMM d');
    }
  };

  const truncateTitle = (title, maxLength = 25) => {
    return title?.length > maxLength ? `${title?.substring(0, maxLength)}...` : title;
  };

  if (collapsed) {
    return (
      <div className="w-16 bg-gray-50 border-r flex flex-col items-center py-4">
        <button
          onClick={onToggleCollapse}
          className="p-2 text-gray-500 hover:text-gray-700 transition-colors mb-4"
          title="Expand sidebar"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <button
          onClick={() => onNewConversation()}
          className="p-2 text-gray-500 hover:text-green-600 transition-colors mb-4"
          title="New conversation"
        >
          <Plus className="w-5 h-5" />
        </button>
        <div className="flex-1 overflow-y-auto space-y-2">
          {conversations?.slice(0, 8)?.map((conversation) => (
            <button
              key={conversation?.id}
              onClick={() => onSelectConversation(conversation)}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                activeConversation?.id === conversation?.id
                  ? 'bg-green-100 text-green-600' :'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title={conversation?.title}
            >
              <MessageSquare className="w-4 h-4" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-gray-50 border-r flex flex-col">
      {/* Sidebar Header */}
      <div className="p-4 border-b bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Chat History</h3>
          <button
            onClick={onToggleCollapse}
            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
            title="Collapse sidebar"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>
        
        <button
          onClick={() => onNewConversation()}
          className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New IPM Chat
        </button>
      </div>
      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto p-2">
        {conversations?.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No conversations yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Start a new chat to get IPM guidance
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {conversations?.map((conversation) => (
              <div
                key={conversation?.id}
                className={`group relative rounded-lg transition-colors ${
                  activeConversation?.id === conversation?.id
                    ? 'bg-green-100 border border-green-200' :'hover:bg-white hover:shadow-sm border border-transparent'
                }`}
                onMouseEnter={() => setHoveredConversation(conversation?.id)}
                onMouseLeave={() => setHoveredConversation(null)}
              >
                <button
                  onClick={() => onSelectConversation(conversation)}
                  className="w-full p-3 text-left"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        activeConversation?.id === conversation?.id
                          ? 'text-green-800' :'text-gray-900'
                      }`}>
                        {truncateTitle(conversation?.title)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatConversationTime(conversation?.last_message_at)}
                      </p>
                    </div>
                    <div className="ml-2 flex items-center">
                      <MessageSquare className={`w-4 h-4 ${
                        activeConversation?.id === conversation?.id
                          ? 'text-green-600' :'text-gray-400'
                      }`} />
                    </div>
                  </div>
                </button>

                {/* Conversation Actions */}
                {hoveredConversation === conversation?.id && (
                  <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        onArchiveConversation(conversation?.id);
                      }}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Archive conversation"
                    >
                      <Archive className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Sidebar Footer */}
      <div className="p-4 border-t bg-white">
        <div className="text-xs text-gray-500 space-y-2">
          <p className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            AI Assistant Online
          </p>
          <p>
            Get expert IPM guidance powered by comprehensive agricultural knowledge
          </p>
        </div>
      </div>
    </div>
  );
}